/**
 * The student's side, as queries and mutations.
 *
 * Two queries and they are deliberately separate: the list is what every
 * screen with a nav needs, and the detail is one vertical's rounds and forms.
 * Fetching the second everywhere would mean loading seven forms to draw a
 * dashboard.
 *
 * Every mutation invalidates both, plus `me` — a submission changes the
 * dashboard, the vertical's own page, and nothing about the session, but the
 * cost of the extra invalidation is one cached request and the cost of
 * forgetting it is a screen that lies.
 */
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { api } from "./api";
import { messageOf } from "./errors";

async function fetchApplications() {
  const { data, error } = await api.applications.get();
  if (error) throw new Error(messageOf(error.value, "Could not load your applications."));
  return data;
}

export const applicationsQuery = queryOptions({
  queryKey: ["applications"],
  queryFn: fetchApplications,
  staleTime: 10_000,
});

export type Applications = Awaited<ReturnType<typeof fetchApplications>>;
export type ApplicationItem = Applications["items"][number];
export type StepperRound = NonNullable<ApplicationItem["application"]>["rounds"][number];

async function fetchApplication(vertical: string) {
  const { data, error } = await api.applications({ vertical }).get();
  if (error) throw new Error(messageOf(error.value, "Could not load that vertical."));
  return data;
}

export const applicationQuery = (vertical: string) =>
  queryOptions({
    queryKey: ["applications", vertical],
    queryFn: () => fetchApplication(vertical),
  });

export type ApplicationDetail = Awaited<ReturnType<typeof fetchApplication>>;
export type DetailRound = ApplicationDetail["rounds"][number];
export type FormQuestion = NonNullable<DetailRound["form"]>["questions"][number];

function invalidateApplications(qc: QueryClient) {
  return qc.invalidateQueries({ queryKey: ["applications"] });
}

export function useSubmitRound(vertical: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { roundId: string; answers: Record<string, unknown> }) => {
      const { data, error } = await api.applications({ vertical }).submit.post(input);
      if (error) throw new Error(messageOf(error.value, "Could not send that."));
      return data;
    },
    onSuccess: () => invalidateApplications(qc),
  });
}

export function useWithdraw(vertical: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await api.applications({ vertical }).withdraw.post();
      if (error) throw new Error(messageOf(error.value, "Could not withdraw."));
      return data;
    },
    onSuccess: () => invalidateApplications(qc),
  });
}

/**
 * The answers a student has typed but not sent, per round, in this browser.
 *
 * There is no drafts table on the server, and that is on purpose: a row in
 * `submissions` is a fact — this person submitted this — and giving it a draft
 * mode makes every query downstream ask "but is it real?". The cost is that a
 * half-written answer does not follow you to another machine, which is the
 * right trade for a form that takes ten minutes, not ten days. It is cleared
 * the moment the real submission lands.
 */
const draftKey = (roundId: string) => `postman.draft.${roundId}`;

export function readDraft(roundId: string): Record<string, unknown> | null {
  try {
    const raw = window.localStorage.getItem(draftKey(roundId));
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
  } catch {
    // Private windows, blocked site data, a corrupt value someone edited by
    // hand: none of them are worth an error screen over a convenience.
    return null;
  }
}

export function writeDraft(roundId: string, answers: Record<string, unknown>) {
  try {
    window.localStorage.setItem(draftKey(roundId), JSON.stringify(answers));
  } catch {
    /* ignore */
  }
}

export function clearDraft(roundId: string) {
  try {
    window.localStorage.removeItem(draftKey(roundId));
  } catch {
    /* ignore */
  }
}
